import { AuthByEmailRequestModel } from '@goparrot-dashboard-api/auth';
import { useEntityForm, ValidationStrategyEnum } from '@goparrot-dashboard/entity-form';
import { useI18n } from '@goparrot-dashboard/i18n';
import { Checkbox, Form } from '@goparrot-dashboard/shared-ui';
import { ENTITY_VALIDATION_GROUPS } from '@goparrot-dashboard/shared-utils';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { MarketButton } from '@market/react-bindings';
import { EMAIL_AUTOCOMPLETE_KEY, ENTER_KEY } from '../constants';

import { SsoSignInButtonWrapper } from '../components/SsoSignInButtonWrapper';
import { EmailFormSegment } from './segments/EmailFormSegment';
import { PasswordFormSegment } from './segments/PasswordFormSegment';

const FORM_VALIDATION_GROUPS = [ENTITY_VALIDATION_GROUPS.LOGIN_EMAIL];

type LoginFormProps = {
  onSubmit(payload: AuthByEmailRequestModel): void;
  isFormLoading?: boolean;
};
export const LoginForm: React.FC<React.PropsWithChildren<LoginFormProps>> = ({ onSubmit, isFormLoading = false }) => {
  const { getIntlString } = useI18n();

  const form = useEntityForm<AuthByEmailRequestModel>({
    model: AuthByEmailRequestModel,
    validationStrategy: ValidationStrategyEnum.onSave,
    validationGroups: FORM_VALIDATION_GROUPS,
    initialState: {
      email: '',
      password: '',
    },
  });

  const [remember, setRemember] = React.useState(true);

  const rememberMeRef = React.useRef<HTMLDivElement>(null);
  const forgotPasswordRef = React.useRef<HTMLAnchorElement>(null);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    form.onSubmit(onSubmit);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === ENTER_KEY) {
      if (forgotPasswordRef.current?.contains(e.target as HTMLElement)) {
        return;
      }

      if (rememberMeRef.current?.contains(e.target as HTMLElement)) {
        setRemember((prevRemember) => !prevRemember);
        return;
      }

      handleSubmit(e);
    }
  };

  React.useEffect(() => {
    const emailForAutoComplete: string | null = sessionStorage.getItem(EMAIL_AUTOCOMPLETE_KEY);
    if (emailForAutoComplete) {
      form.setFieldValue('email', emailForAutoComplete);
      sessionStorage.removeItem(EMAIL_AUTOCOMPLETE_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
        <EmailFormSegment<AuthByEmailRequestModel> form={form} />
        <PasswordFormSegment<AuthByEmailRequestModel> form={form} autoComplete="current-password" placeholder={getIntlString('placeholder.currentPassword')} />

        <div className="tw-flex tw-items-center tw-justify-between tw-mt-4">
          <div ref={rememberMeRef}>
            <Checkbox checked={remember} onChange={setRemember} label={getIntlString('label.rememberMe')} />
          </div>

          <div className="tw-text-sm">
            <Link
              ref={forgotPasswordRef}
              to="forgot-password"
              className="tw-font-medium tw-text-base tw-text-light-blue-600 hover:tw-text-light-blue-500"
              data-testid="forgot-password-link"
            >
              {getIntlString('link.forgotPassword')}
            </Link>
          </div>
        </div>
        <MarketButton
          isLoading={isFormLoading || undefined}
          disabled={isFormLoading || (form.isDirtyForm && !form.isFormValid) || undefined}
          type="submit"
          className="tw-mt-4"
          rank="primary"
          data-testid="sign-in-button"
          role="button"
        >
          {getIntlString('button.signIn')}
        </MarketButton>
      </Form>
      <Form>
        <SsoSignInButtonWrapper />
      </Form>
    </>
  );
};
